import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideMonacoEditor } from 'ngx-monaco-editor-v2';
import { ApplicationConfig, inject, LOCALE_ID, provideAppInitializer } from '@angular/core';
import { provideRouter } from '@angular/router';
import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalGuardConfiguration,
    MsalInterceptor,
    MsalInterceptorConfiguration,
    MsalService
} from '@azure/msal-angular';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { provideStore } from '@ngxs/store';
import { SI_LOCALE_CONFIG, SiLocaleId, SiLocaleService } from '@siemens/element-ng/localization';
import {
    provideMissingTranslationHandlerForElement,
    provideNgxTranslateForElement
} from '@siemens/element-translate-ng/ngx-translate';
import { of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { UnauthorizedInterceptor } from './auth/services/unauthorized-interceptor.service';
import { DEFAULT_LOCALE, localeConfig } from './core/config/locale.config';
import { provideJunoSdk } from '@foundation/juno-sdk/angular';
import { UserState } from './auth/state/user.state';
import { ProjectsState } from './projects/state/projects.state';
import { ScdState } from './shared/state/scd/scd.state';
import { provideFeatureFlags } from './core/feature-flags';
import { FLAG_CATALOG } from './flag-catalog';

const msalInstanceFactory = () => new PublicClientApplication(environment.msalConfig);

const msalInterceptorConfigFactory = (): MsalInterceptorConfiguration => {
    const protectedResourceMap = new Map<string, string[]>();
    for (const item of environment.apiConfig) {
        // Normalise to a single trailing wildcard — avoid double `/*` if uri already ends with `/*`
        const uri = item.uri.endsWith('/*') ? item.uri : item.uri.endsWith('/') ? `${item.uri}*` : `${item.uri}/*`;
        protectedResourceMap.set(uri, item.scopes);
    }
    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap
    };
};

// TODO: Add the index of your API configuration that requires authentication here. 0 is currently the graph API.
const msalGuardConfigFactory = (): MsalGuardConfiguration => ({
    interactionType: InteractionType.Redirect,
    authRequest: { scopes: [...environment.apiConfig[0].scopes] },
    loginFailedRoute: '/unauthorized'
});

function initializeMsal(msalService: MsalService) {
    return () => {
        return msalService.handleRedirectObservable().pipe(
            map(() => {
                const accounts = msalService.instance.getAllAccounts();
                if (accounts.length > 0) {
                    const activeAccount = msalService.instance.getActiveAccount() || accounts[0];
                    msalService.instance.setActiveAccount(activeAccount);
                }
                return undefined;
            }),
            catchError(error => {
                console.error('Error during MSAL initialization:', error);
                return of(undefined);
            }),
            take(1)
        );
    };
}

function initializeLanguage(translateService: TranslateService, localeService: SiLocaleService) {
    return () => {
        const locale = localeService.locale;
        translateService.setFallbackLang(DEFAULT_LOCALE);
        translateService.use(locale);
        return of(undefined);
    };
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(routes),

        // Monaco editor — assets served from assets/vs (configured in project.json).
        // NOTE: ngx-monaco-editor-v2 rewrites baseUrl "assets" (exact match) to
        // "./assets/monaco/min/vs" for backward compatibility, which does not match
        // our actual asset output path. Use "assets/vs" (not the literal "assets")
        // so the library uses our path verbatim instead of the compat rewrite.
        provideMonacoEditor({ baseUrl: 'assets/vs' }),

        // Locale configuration for Element framework
        { provide: LOCALE_ID, useClass: SiLocaleId, deps: [SiLocaleService] },
        { provide: SI_LOCALE_CONFIG, useValue: localeConfig },

        // Translation configuration with ngx-translate v18.
        // TranslateHttpLoader v18 has a no-arg constructor and is configured via
        // provideTranslateHttpLoader({ prefix, suffix }); the HttpClient is injected
        // internally, so no useFactory/deps wiring is needed.
        provideTranslateService({
            loader: provideTranslateHttpLoader({
                prefix: '/assets/i18n/',
                suffix: `.json?hash=${environment.hash}`
            }),
            fallbackLang: DEFAULT_LOCALE,
            missingTranslationHandler: provideMissingTranslationHandlerForElement()
        }),

        // Enable ngx-translate for Element components
        provideNgxTranslateForElement(),

        provideHttpClient(withInterceptorsFromDi()),
        ...provideJunoSdk({
            // Find the JUNO API entry by scope substring rather than a fragile array index.
            // Fall back to index 1 (the conventional JUNO slot) if the scope pattern doesn't match.
            baseUrl: (
                environment.apiConfig.find(c => c.scopes.some(s => s.includes('juno'))) ?? environment.apiConfig[1]
            ).uri.replace('/*', '')
        }),
        provideStore(
            [UserState, ProjectsState, ScdState],
            { developmentMode: !environment.production, compatibility: { strictContentSecurityPolicy: true } },
            withNgxsReduxDevtoolsPlugin()
        ),
        {
            provide: MSAL_INSTANCE,
            useFactory: msalInstanceFactory
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: msalGuardConfigFactory
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: msalInterceptorConfigFactory
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: UnauthorizedInterceptor,
            multi: true
        },
        provideAppInitializer(() => {
            const initializerFn = initializeMsal(inject(MsalService));
            return initializerFn();
        }),
        // Feature flags: fetches ui.* flags from the backend once MSAL has
        // finished its redirect handling (the initializer awaits inProgress$
        // reaching None internally). On failure or timeout, catalog defaults
        // are used — the SPA never blocks on a source outage.
        provideFeatureFlags(FLAG_CATALOG),
        provideAppInitializer(() => {
            const initializerFn = initializeLanguage(inject(TranslateService), inject(SiLocaleService));
            return initializerFn();
        })
    ]
};
